import * as THREE from "three";
import { SceneManager } from "../core/scene_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import type { LevelEntity } from "../types/level_entity.js";
import type { IController } from "../vfx_3d/core/types/controller_types.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";

export interface StickyMineAttachment
{
  entity: LevelEntity;
  /** Stick point in the body's local frame, so the mine rides the block. */
  local_point: THREE.Vector3;
  /** Outward face normal in the body's local frame. */
  local_normal: THREE.Vector3;
}

enum StickyMineState { Flying, Stuck, Done }

/**
 * Visual-only sticky bomb of the Mine booster. It has no collider: it flies
 * onto a block, follows that block's body while the fuse blinks and then
 * reports the blast to the owner, which pushes the blocks around.
 */
export class StickyMine
{
  private static readonly FLIGHT_SECONDS = 0.5;
  private static readonly SPLAT_SECONDS = 0.14;
  private static readonly BLINK_START_HZ = 2.5;
  private static readonly BLINK_END_HZ = 9;
  /** Share of the cube width the mine covers. */
  private static readonly SIZE_TO_CUBE = 0.62;
  private static readonly SURFACE_OFFSET_TO_SIZE = 0.12;
  private static readonly RED = new THREE.Color( 1.0, 0.03, 0.01 );
  private static readonly UP = new THREE.Vector3( 0, 1, 0 );

  private static prototype_mesh: THREE.Object3D | null = null;
  private static thin_axis = new THREE.Vector3( 0, 1, 0 );
  private static authored_size = 1;

  private readonly root = new THREE.Group();
  private readonly spin = new THREE.Group();
  private readonly material: THREE.MeshStandardMaterial;
  private readonly blink_uniforms = {
    uBlinkColor: { value: StickyMine.RED.clone() },
    uBlinkAmount: { value: 0 },
  };

  private state = StickyMineState.Flying;
  private age = 0;
  private stuck_age = 0;
  private readonly start = new THREE.Vector3();
  private readonly last_point = new THREE.Vector3();
  private readonly last_normal = new THREE.Vector3( 0, 0, 1 );
  private readonly spin_axis = new THREE.Vector3();
  private trail: IController | null = null;
  private readonly size: number;

  private static readonly scratch_q = new THREE.Quaternion();
  private static readonly scratch_q2 = new THREE.Quaternion();
  private static readonly scratch_v = new THREE.Vector3();
  private static readonly scratch_v2 = new THREE.Vector3();

  public constructor(
    private readonly attachment: StickyMineAttachment,
    private readonly fuse_seconds: number,
    cube_width: number,
    private readonly on_explode: ( position: THREE.Vector3, normal: THREE.Vector3 ) => void
  )
  {
    this.size = cube_width * StickyMine.SIZE_TO_CUBE;
    this.material = StickyMine.createMaterial( this.blink_uniforms );

    const model = StickyMine.createModel();
    model.scale.setScalar( this.size / StickyMine.authored_size );
    model.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        child.material = this.material;
        child.castShadow = true;
      }
    } );

    // The model's thin axis becomes the wrapper's +Z, the face normal below.
    this.spin.quaternion.setFromUnitVectors( StickyMine.thin_axis, new THREE.Vector3( 0, 0, 1 ) );
    this.spin.add( model );
    this.root.add( this.spin );

    this.resolveAttachPoint();
    this.start.copy( this.last_point )
      .add( new THREE.Vector3( THREE.MathUtils.randFloatSpread( 2.4 ), 8, 3 ) );
    this.spin_axis.set( Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5 ).normalize();
    this.root.position.copy( this.start );
    SceneManager.world.add( this.root );

    this.trail = Vfx3DManager.addFireworkTrail( this.start );
  }

  public get isFinished(): boolean
  {
    return this.state === StickyMineState.Done;
  }

  /** Blows the mine right now (the level is ending or its block is gone). */
  public detonate(): void
  {
    if ( this.state === StickyMineState.Done )
      return;

    this.state = StickyMineState.Done;
    this.stopTrail();
    this.on_explode( this.root.position.clone(), this.last_normal.clone() );
    this.dispose();
  }

  public update( dt: number ): void
  {
    if ( this.state === StickyMineState.Done )
      return;

    this.age += dt;
    this.resolveAttachPoint();

    if ( this.state === StickyMineState.Flying )
      this.updateFlight();
    else
      this.updateStuck( dt );
  }

  public dispose(): void
  {
    this.state = StickyMineState.Done;
    this.stopTrail();
    this.root.removeFromParent();
    this.material.dispose();
  }

  private updateFlight(): void
  {
    const t = Math.min( 1, this.age / StickyMine.FLIGHT_SECONDS );
    const eased = t * t;
    const control = StickyMine.scratch_v.set( this.last_point.x, this.start.y, this.last_point.z + 2.5 );
    const u = 1 - eased;
    this.root.position.set( 0, 0, 0 )
      .addScaledVector( this.start, u * u )
      .addScaledVector( control, 2 * u * eased )
      .addScaledVector( this.last_point, eased * eased );

    // Tumble in the air, then settle flat against the face.
    const facing = this.faceQuaternion( StickyMine.scratch_q );
    const tumble = StickyMine.scratch_q2.setFromAxisAngle( this.spin_axis, ( 1 - t ) * 9 );
    this.root.quaternion.copy( facing ).multiply( tumble );
    this.trail?.setPosition( this.root.position );

    if ( t >= 1 )
    {
      this.state = StickyMineState.Stuck;
      this.stuck_age = 0;
      this.stopTrail();
      if ( !this.attachment.entity.isAlive() )
        this.detonate();
    }
  }

  private updateStuck( dt: number ): void
  {
    this.stuck_age += dt;
    if ( !this.attachment.entity.isAlive() || this.stuck_age >= this.fuse_seconds )
    {
      this.detonate();
      return;
    }

    this.root.position.copy( this.last_point );
    this.faceQuaternion( this.root.quaternion );

    // Splat on impact: squashed against the face, springing back.
    const splat = Math.min( 1, this.stuck_age / StickyMine.SPLAT_SECONDS );
    const squash = 1 - 0.45 * ( 1 - splat ) * ( 1 - splat );
    const bulge = 1 + 0.25 * ( 1 - splat );

    // Blink faster and faster towards the blast: green ↔ red.
    const k = this.stuck_age / this.fuse_seconds;
    const hz = THREE.MathUtils.lerp( StickyMine.BLINK_START_HZ, StickyMine.BLINK_END_HZ, k * k );
    const phase = this.blinkPhase( hz );
    const red = phase > 0 ? 1 : 0;
    this.blink_uniforms.uBlinkAmount.value = red;
    this.material.emissiveIntensity = red ? 1.6 : 1.3;

    const pulse = 1 + 0.12 * red + 0.08 * k;
    this.root.scale.set( bulge * pulse, bulge * pulse, squash * pulse );
  }

  private blink_time = 0;
  private blink_last_age = 0;

  /** Integrates the rising frequency so the blink never jumps phase. */
  private blinkPhase( hz: number ): number
  {
    this.blink_time += ( this.stuck_age - this.blink_last_age ) * hz;
    this.blink_last_age = this.stuck_age;
    return Math.sin( this.blink_time * Math.PI * 2 );
  }

  private resolveAttachPoint(): void
  {
    const { entity, local_point, local_normal } = this.attachment;
    if ( !entity.isAlive() )
      return;

    const p = entity.body.translation();
    const r = entity.body.rotation();
    const q = StickyMine.scratch_q2.set( r.x, r.y, r.z, r.w );
    this.last_normal.copy( local_normal ).applyQuaternion( q ).normalize();
    this.last_point.copy( local_point ).applyQuaternion( q ).add( StickyMine.scratch_v2.set( p.x, p.y, p.z ) )
      .addScaledVector( this.last_normal, this.size * StickyMine.SURFACE_OFFSET_TO_SIZE );
  }

  /** Wrapper +Z along the face normal, wrapper +Y kept as upright as possible. */
  private faceQuaternion( out: THREE.Quaternion ): THREE.Quaternion
  {
    const z = this.last_normal;
    const up = Math.abs( z.dot( StickyMine.UP ) ) > 0.95 ? StickyMine.scratch_v.set( 0, 0, -1 ) : StickyMine.UP;
    const x = StickyMine.scratch_v2.crossVectors( up, z ).normalize();
    const y = new THREE.Vector3().crossVectors( z, x );
    return out.setFromRotationMatrix( new THREE.Matrix4().makeBasis( x, y, z ) );
  }

  private stopTrail(): void
  {
    this.trail?.stop();
    this.trail = null;
  }

  private static createModel(): THREE.Object3D
  {
    if ( !this.prototype_mesh )
    {
      const model = ResourceManager.getModel( ResourceID.MineModel );
      const bounds = new THREE.Box3().setFromObject( model );
      const size = bounds.getSize( new THREE.Vector3() );
      const centre = bounds.getCenter( new THREE.Vector3() );
      model.position.sub( centre );

      const wrapper = new THREE.Group();
      wrapper.add( model );
      this.prototype_mesh = wrapper;
      this.authored_size = Math.max( size.x, size.y, size.z );
      this.thin_axis = size.x <= size.y && size.x <= size.z
        ? new THREE.Vector3( 1, 0, 0 )
        : size.y <= size.z
          ? new THREE.Vector3( 0, 1, 0 )
          : new THREE.Vector3( 0, 0, 1 );
    }

    return this.prototype_mesh.clone( true );
  }

  private static createMaterial( uniforms: StickyMine["blink_uniforms"] ): THREE.MeshStandardMaterial
  {
    const orm = ResourceManager.getTexture( ResourceID.MineORM );
    const material = new THREE.MeshStandardMaterial( {
      map: ResourceManager.getTexture( ResourceID.MineBaseColor ),
      emissiveMap: ResourceManager.getTexture( ResourceID.MineEmissive ),
      emissive: new THREE.Color( 1, 1, 1 ),
      emissiveIntensity: 1.3,
      roughnessMap: orm,
      metalnessMap: orm,
      roughness: 1,
      metalness: 1,
    } );

    // Recolours only the glowing core (where the emissive map is lit): the
    // green light and the green paint under it turn red while blinking.
    material.onBeforeCompile = shader =>
    {
      shader.uniforms.uBlinkColor = uniforms.uBlinkColor;
      shader.uniforms.uBlinkAmount = uniforms.uBlinkAmount;
      shader.fragmentShader = shader.fragmentShader
        .replace( "void main() {", "uniform vec3 uBlinkColor;\nuniform float uBlinkAmount;\nvoid main() {" )
        .replace( "#include <emissivemap_fragment>", `
#ifdef USE_EMISSIVEMAP
  vec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );
  float blinkMask = max( emissiveColor.r, max( emissiveColor.g, emissiveColor.b ) );
  totalEmissiveRadiance *= mix( emissiveColor.rgb, uBlinkColor * blinkMask, uBlinkAmount );
  diffuseColor.rgb = mix( diffuseColor.rgb, uBlinkColor, blinkMask * uBlinkAmount );
#endif
` );
    };
    material.customProgramCacheKey = () => "sticky_mine_blink";
    return material;
  }
}
